/** Record how a Statutory Demand (or petition) was served — method, date, server, evidence files → OneDrive "Service evidence". */
import { useRef, useState } from "react";
import { FileCheck2, Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { addServiceRecord, legalUploadChunk, uploadInboxAttachment } from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const today = () => new Date().toISOString().slice(0, 10);
const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const DOCS = { sd2: "Statutory Demand (SD2)", petition: "Bankruptcy petition", other: "Other document" };

export default function ServiceRecordDialog({ caseRow, methods = [], document = "sd2", onClose, onSaved, onGateBlocked }) {
  const [form, setForm] = useState({ document, method: methods[0]?.key || "personal", served_at: today(), served_by: "", location: "", court_order_ref: "", notes: "" });
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const method = methods.find((m) => m.key === form.method);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const addFiles = (list) => setFiles((cur) => [...cur, ...Array.from(list || []).filter((f) => !cur.some((x) => x.name === f.name && x.size === f.size))]);
  const petitionRule = form.document === "petition" && form.method !== "personal";   // C1: personal service unless the court ordered otherwise
  const needsOrder = !!method?.requires_court_order || petitionRule;
  const valid = form.method && form.served_at && files.length > 0 && (!needsOrder || form.court_order_ref.trim().length >= 3);

  const submit = async () => {
    setBusy(true);
    try {
      const evidence = [];
      for (let i = 0; i < files.length; i++) {
        setProgress({ name: files[i].name, i: i + 1, n: files.length, pct: 0 });
        evidence.push(await uploadInboxAttachment(legalUploadChunk, files[i], (pct) => setProgress((p) => ({ ...p, pct }))));
      }
      setProgress(null);
      const r = await addServiceRecord(caseRow.case_id, { ...form, evidence });
      (r.warnings || []).forEach((w) => toast.warning(w));
      const n = (r.created_events || []).length;
      toast.success(`${DOCS[form.document]} service recorded · ${evidence.length} file${evidence.length === 1 ? "" : "s"} saved to OneDrive${n ? ` · ${n} diary action${n === 1 ? "" : "s"}` : ""}${r.route ? ` · route → ${r.route.label}` : ""}`);
      if (r.route_blocked) toast.info(`Route not moved yet — ${r.route_blocked.label}: ${r.route_blocked.blocking.join(", ")}`);
      onSaved?.(r);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.gate) { toast.error(errorDetailMessage(e, "")); onGateBlocked?.(d.gate.gate, d.gate); }
      else toast.error(errorDetailMessage(e, e.message));
    } finally { setBusy(false); setProgress(null); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-xl" data-testid="service-record-dialog" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2"><FileCheck2 className="w-4 h-4 text-orange-700" />Record service — {caseRow.customer_name}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <label className="block">
            <span className="block text-[11px] font-medium text-slate-600 mb-1">Document</span>
            <Select value={form.document} onValueChange={set("document")}>
              <SelectTrigger className="h-8 text-xs" data-testid="service-document"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(DOCS).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>)}</SelectContent>
            </Select>
          </label>
          <label className="block">
            <span className="block text-[11px] font-medium text-slate-600 mb-1">Date served</span>
            <Input type="date" value={form.served_at} max={today()} onChange={set("served_at")} className="h-8 text-xs" data-testid="service-date" />
          </label>
          <label className="block col-span-2">
            <span className="block text-[11px] font-medium text-slate-600 mb-1">Method of service</span>
            <Select value={form.method} onValueChange={set("method")}>
              <SelectTrigger className="h-8 text-xs" data-testid="service-method"><SelectValue /></SelectTrigger>
              <SelectContent>{methods.map((m) => <SelectItem key={m.key} value={m.key} className="text-xs">{m.label}</SelectItem>)}</SelectContent>
            </Select>
            {method && <span className="block text-[10px] text-muted-foreground mt-1">Evidence expected: {method.evidence}{needsOrder ? " · court order required" : ""}</span>}
            {petitionRule && <span className="block text-[10px] text-amber-800 mt-0.5" data-testid="service-petition-rule">A bankruptcy petition is served personally unless the court has ordered substituted service — enter the N244 order reference / date below.</span>}
          </label>
          <label className="block">
            <span className="block text-[11px] font-medium text-slate-600 mb-1">Served by (process server / person)</span>
            <Input value={form.served_by} onChange={set("served_by")} className="h-8 text-xs" placeholder="e.g. ABC Process Servers" data-testid="service-served-by" />
          </label>
          <label className="block">
            <span className="block text-[11px] font-medium text-slate-600 mb-1">Where</span>
            <Input value={form.location} onChange={set("location")} className="h-8 text-xs" placeholder="Address / place of service" data-testid="service-location" />
          </label>
          {needsOrder && (
            <label className="block col-span-2">
              <span className="block text-[11px] font-medium text-slate-600 mb-1">Court order reference</span>
              <Input value={form.court_order_ref} onChange={set("court_order_ref")} className="h-8 text-xs" placeholder="Order permitting this method" data-testid="service-court-order" />
            </label>
          )}
          <label className="block col-span-2">
            <span className="block text-[11px] font-medium text-slate-600 mb-1">Notes</span>
            <Textarea value={form.notes} onChange={set("notes")} rows={2} className="text-xs" data-testid="service-notes" />
          </label>
          <div className="col-span-2 border rounded p-2 bg-slate-50/60" data-testid="service-evidence">
            <div className="flex items-center gap-2 flex-wrap">
              <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="service-attach-btn">
                <Paperclip className="w-3 h-3 mr-1" />Add evidence
              </Button>
              <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="service-file-input" />
              <span className="text-[10px] text-muted-foreground">{files.length ? `${files.length} file${files.length === 1 ? "" : "s"}` : "certificate of service, proof of posting, photos… saved to the file's OneDrive folder (required)"}</span>
            </div>
            {files.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="inline-flex items-center gap-1 border rounded bg-white px-2 py-0.5 text-[11px] max-w-[240px]" data-testid={`service-evidence-${i}`}>
                    <span className="truncate">{f.name}</span><span className="text-slate-400">{fmtSize(f.size)}</span>
                    <button type="button" className="text-slate-400 hover:text-red-600" onClick={() => setFiles(files.filter((_, j) => j !== i))} disabled={busy} data-testid={`service-evidence-remove-${i}`}><X className="w-3 h-3" /></button>
                  </li>
                ))}
              </ul>
            )}
            {progress && <div className="mt-2 text-[10px] text-blue-700" data-testid="service-upload-progress">Uploading {progress.name} ({progress.i}/{progress.n}) — {progress.pct}%</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="service-cancel">Cancel</Button>
          <Button size="sm" className="bg-orange-700 hover:bg-orange-800" onClick={submit} disabled={busy || !valid} data-testid="service-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileCheck2 className="w-3.5 h-3.5 mr-1" />}{busy ? (progress ? "Uploading…" : "Saving…") : "Save service record"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
